import { memo, useCallback, useState } from "react";
import FilterListIcon from "@mui/icons-material/FilterList";

import { TreeViewDeModulos } from "./TreeViewDeModulos";
import {
  IFuncionalidade,
  IOperacao,
  IProduto,
} from "../../../shared/services/api/permissoes/PermissoesService";
import { InputDeBusca } from "../../../shared/components";
import { ModalDeFiltro } from "./ModalFiltroPermissoes";
import { ContagemDeFiltros } from "./ContagemDeFiltros";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Switch,
  Typography,
  Tabs,
  Tab,
  Divider,
} from "@gvdasa/react-components";

interface IAccordionPermissao {
  erroAoSalvarPermissoes: boolean;
  isLoading: boolean;
  produtos: IProduto[];
  handleValoresCheckbox: (event, funcionalidade, id) => void;
  mensagemDeErroPermissoes: string;
  disabled: boolean;
  handleMarcarTodos: (
    e: React.ChangeEvent<HTMLInputElement>,
    modulo: {
      id: string;
      nome: string;
      funcionalidades: IFuncionalidade[];
    },
    produto: IProduto
  ) => void;
  filtrosPorOperacao: IOperacao[];
  pesquisar: string;
  setPesquisar: React.Dispatch<React.SetStateAction<string>>;
  setFiltrado: React.Dispatch<React.SetStateAction<boolean>>;
  filtrado: boolean;
}

export const AccordionPermissoes: React.FC<IAccordionPermissao> = memo(
  ({
    erroAoSalvarPermissoes,
    isLoading,
    produtos,
    handleValoresCheckbox,
    mensagemDeErroPermissoes,
    disabled,
    handleMarcarTodos,
    filtrosPorOperacao,
    pesquisar,
    setPesquisar,
    filtrado,
    setFiltrado,
  }) => {
    interface TabPanelProps {
      children?: React.ReactNode;
      index: number;
      value: number;
    }

    function TabPanel(props: TabPanelProps) {
      const { children, value, index, ...other } = props;

      return (
        <div
          style={{ padding: "0px", height: "450px" }}
          role="tabpanel"
          hidden={value !== index}
          id={`vertical-tabpanel-${index}`}
          aria-labelledby={`vertical-tab-${index}`}
          {...other}
        >
          {value === index && (
            <Box sx={{ p: 1, alignItems: "center" }}>
              <Typography component="span">{children}</Typography>
            </Box>
          )}
        </div>
      );
    }

    const [value, setValue] = useState(0);
    const [selectedValue, setSelectedValue] = useState("");
    const [open, setOpen] = useState(false);
    const [exibirApenasMarcados, setExibirApenasMarcados] = useState(false);
    const [lista, setLista] = useState<string[]>([]);
    const [listaFiltrada, setListaFiltrada] = useState<string[]>([]);
    const [listaMultiplaDeProdutos, setListaMultiplaDeProdutos] = useState<
      string[]
    >([]);

    const filtros = useCallback(() => {
      return listaMultiplaDeProdutos.flatMap((itemLista) =>
        produtos.filter((pf) => pf.nome === itemLista)
      );
    }, [listaMultiplaDeProdutos]);

  const funcaoDeFiltragemPorOperacao = (pesquisar: string) => {
  if (pesquisar.length === 0) {
    return produtos;
  }

  const filtroPorOperacao: IProduto[] = [];
  const produtosAdicionados = new Set<string>();

  produtos.forEach((p) => {
    p.modulos.forEach((m) => {
      m.funcionalidades.forEach((f) => {
        f.operacoes.forEach((op) => {
          // Filtrar por nome da operação que contenha o texto pesquisado
          if (op.nome.toLowerCase().includes(pesquisar.toLowerCase())) {
            if (!produtosAdicionados.has(p.nome)) {
              filtroPorOperacao.push(p);
              produtosAdicionados.add(p.nome);
            }
          }
          
          // Se há filtros por operação, também verificar se a operação está nos filtros
          filtrosPorOperacao.forEach((ops) => {
            if (ops.nome === op.nome && op.nome.toLowerCase().includes(pesquisar.toLowerCase())) {
              if (!produtosAdicionados.has(p.nome)) {
                filtroPorOperacao.push(p);
                produtosAdicionados.add(p.nome);
              }
            }
          });
        });
      });
    });
  });

  return filtroPorOperacao;
};

    const filtroPorProdutoEPorOperacao = funcaoDeFiltragemPorOperacao(
      pesquisar
    ).filter((fu) => listaMultiplaDeProdutos.includes(fu.nome));

    const apenasProdutosPermissoesHabilitadas = (produtos: IProduto[]) => {
      const marcados: IProduto[] = [];
      produtos.flatMap((p, i) =>
        p.modulos.flatMap((m) =>
          m.funcionalidades.flatMap((f) =>
            f.operacoes.flatMap((o) => {
              if (o.temPermissao === true && !marcados.includes(p)) {
                marcados.push(p);
              }
            })
          )
        )
      );
      return marcados;
    };

    const handleExibirPermissoes = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setExibirApenasMarcados(event.target.checked);
    };

    const handleClickOpen = () => {
      setOpen(true);
      setListaFiltrada(listaMultiplaDeProdutos);
    };

    const handleClose = (value: string) => {
      setOpen(false);
      setSelectedValue(value);
    };

    const handleTabs = (event: React.SyntheticEvent, newValue: number) => {
      setValue(newValue);
    };

    return (
      <Box>
        <Typography
          variant="h5"
          fontFamily="roboto"
          color="primary"
          fontWeight="bold"
          marginBottom={2}
        >
          Permissões
        </Typography>
        {!isLoading && (
          <Grid size={12} display="flex" gap={2} alignItems="center">
            <ModalDeFiltro
              onClose={handleClose}
              open={open}
              selectedValue={selectedValue}
              produtos={produtos}
              lista={lista}
              setPesquisar={setPesquisar}
              setListaMultiplaDeProdutos={setListaMultiplaDeProdutos}
              listaFiltrada={listaFiltrada}
              setListaFiltrada={setListaFiltrada}
              setLista={setLista}
            />
            <InputDeBusca
              pesquisa={pesquisar}
              setPesquisa={setPesquisar}
              placeholder={"Busque por operações ex: Incluir, Editar..."}
            />
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton
                disableRipple={true}
                onClick={handleClickOpen}
                sx={{
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <FilterListIcon fontSize="medium" sx={{ color: "#018781" }} />
                <ContagemDeFiltros lista={filtros()} />
              </IconButton>
              <FormControlLabel
                control={
                  <Switch
                    size="medium"
                    checked={exibirApenasMarcados}
                    onChange={handleExibirPermissoes}
                  />
                }
                label="Somente com permissão"
              />
            </Box>
          </Grid>
        )}
        {isLoading && <Skeleton height={70} />}
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="flex-start"
          padding={0}
          marginTop="2rem"
          gap={2}
        >
          <Grid size={12} width="400px">
            {!isLoading && (
              <FormControl
                fullWidth
                component={Paper}
                data-testid="teste-nome"
                margin="none"
                error={erroAoSalvarPermissoes}
                sx={{ padding: "0px" }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  textAlign="center"
                  p={1}
                >
                  <Typography
                    variant="h6"
                    padding="4px"
                    fontWeight={600}
                    color="rgba(0, 0, 0, 0.54)"
                  >
                    Produtos
                  </Typography>
                  <Divider variant="fullWidth" />
                </Box>
                <Tabs
                  
                  scrollButtons={false}
                  orientation="vertical"
                  variant="scrollable"
                  value={value}
                  onChange={handleTabs}
                  sx={{
                    borderRight: 1,
                    borderColor: "divider",
                    width: "100%",
                    padding: "2.2px",
                    maxHeight: "395px",
                    overflowY: "scroll",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    // Esconde a barra de scroll no Firefox
                    scrollbarWidth: "none",
                    // Esconde a barra de scroll no IE e Edge antigo
                    msOverflowStyle: "none",
                    borderWidth: 0,
                    "& .MuiButtonBase-root": {
                      borderWidth: 0, // ou defina o valor desejado
                    },
                  }}
                >
                  {listaMultiplaDeProdutos.length === 0 &&
                  filtroPorProdutoEPorOperacao.length === 0
                    ? funcaoDeFiltragemPorOperacao(pesquisar)?.map(
                        (produto, indice) => {
                          return (
                            <Tab
                              key={indice}
                              label={produto.nome}
                              wrapped
                              sx={{
                                alignItems: "flex-start",
                                marginRight: "5px",
                                textTransform: "none",
                                textAlign: "left",
                                fontSize: "1rem",
                                display:
                                  exibirApenasMarcados &&
                                  apenasProdutosPermissoesHabilitadas(
                                    produtos
                                  ).includes(produto)
                                    ? "flex"
                                    : exibirApenasMarcados &&
                                      !apenasProdutosPermissoesHabilitadas(
                                        produtos
                                      ).includes(produto)
                                    ? "none"
                                    : "flex",
                              }}
                            />
                          );
                        }
                      )
                    : listaMultiplaDeProdutos.length > 0 &&
                      filtroPorProdutoEPorOperacao.length > 0
                    ? filtroPorProdutoEPorOperacao?.map((produto, indice) => {
                        return (
                          <Tab
                            key={indice}
                            label={produto.nome}
                            wrapped
                            sx={{
                              alignItems: "flex-start",
                              marginRight: "5px",
                              textTransform: "none",
                              fontSize: "1rem",
                            }}
                          />
                        );
                      })
                    : filtros()?.map((produto, indice) => {
                        return (
                          <Tab
                            key={indice}
                            label={produto.nome}
                            wrapped
                            sx={{
                              alignItems: "flex-start",
                              marginRight: "5px",
                              textTransform: "none",
                              fontSize: "1rem",
                            }}
                          />
                        );
                      })}
                </Tabs>
              </FormControl>
            )}
            {isLoading && <Skeleton height={70} />}
          </Grid>
          <Grid size={12}>
            {!isLoading &&
            listaMultiplaDeProdutos.length === 0 &&
            filtroPorProdutoEPorOperacao.length === 0
              ? funcaoDeFiltragemPorOperacao(pesquisar)?.map(
                  (produto, indice) => {
                    return (
                      <Box
                        component={Paper}
                        sx={{
                          maxHeight: "450px",
                          height: "100%",
                          overflowY: "scroll",
                          "&::-webkit-scrollbar": {
                            display: "none",
                          },
                          // Esconde a barra de scroll no Firefox
                          scrollbarWidth: "none",
                          // Esconde a barra de scroll no IE e Edge antigo
                          msOverflowStyle: "none",
                          borderWidth: 0,
                          "& .MuiButtonBase-root": {
                            borderWidth: 0, // ou defina o valor desejado
                          },
                        }}
                      >
                        <TabPanel value={value} index={indice} key={indice}>
                          <Box display="flex" flexDirection="column" gap={1.5}>
                            <Box
                              display="flex"
                              flexDirection="column"
                              textAlign="center"
                            >
                              <Typography
                                variant="h6"
                                padding="4px"
                                fontWeight={600}
                                color="rgba(0, 0, 0, 0.54)"
                              >
                                Módulos e funcionalidades
                              </Typography>
                              <Divider variant="fullWidth" />
                            </Box>
                            <TreeViewDeModulos
                              key={indice}
                              disabled={disabled}
                              produto={produto}
                              handleValoresCheckbox={handleValoresCheckbox}
                              erroAoSalvarPermissoes={erroAoSalvarPermissoes}
                              mensagemDeErroPermissoes={
                                mensagemDeErroPermissoes
                              }
                              handleMarcarTodos={handleMarcarTodos}
                              filtro={exibirApenasMarcados}
                              filtrosPorOperacao={filtrosPorOperacao}
                              apenasProdutosPermissoesHabilitadas={
                                apenasProdutosPermissoesHabilitadas
                              }
                              produtos={produtos}
                              setFiltrado={setFiltrado}
                              filtrado={filtrado}
                            />
                          </Box>
                        </TabPanel>
                      </Box>
                    );
                  }
                )
              : listaMultiplaDeProdutos.length > 0 &&
                filtroPorProdutoEPorOperacao.length > 0
              ? filtroPorProdutoEPorOperacao?.map((produto, indice) => {
                  return (
                    <Box component={Paper}>
                      <TabPanel value={value} index={indice} key={indice}>
                        <Typography ml={2} fontWeight="400">
                          Módulos e funcionalidades
                        </Typography>
                        <TreeViewDeModulos
                          key={indice}
                          disabled={disabled}
                          produto={produto}
                          handleValoresCheckbox={handleValoresCheckbox}
                          erroAoSalvarPermissoes={erroAoSalvarPermissoes}
                          mensagemDeErroPermissoes={mensagemDeErroPermissoes}
                          handleMarcarTodos={handleMarcarTodos}
                          filtro={exibirApenasMarcados}
                          filtrosPorOperacao={filtrosPorOperacao}
                          apenasProdutosPermissoesHabilitadas={
                            apenasProdutosPermissoesHabilitadas
                          }
                          produtos={produtos}
                          setFiltrado={setFiltrado}
                          filtrado={filtrado}
                        />
                      </TabPanel>
                    </Box>
                  );
                })
              : filtros()?.map((produto, indice) => {
                  return (
                    <Box component={Paper}>
                      <TabPanel value={value} index={indice} key={indice}>
                        <Typography ml={2} fontWeight="400">
                          Módulos e funcionalidades
                        </Typography>
                        <TreeViewDeModulos
                          key={indice}
                          disabled={disabled}
                          produto={produto}
                          handleValoresCheckbox={handleValoresCheckbox}
                          erroAoSalvarPermissoes={erroAoSalvarPermissoes}
                          mensagemDeErroPermissoes={mensagemDeErroPermissoes}
                          handleMarcarTodos={handleMarcarTodos}
                          filtro={exibirApenasMarcados}
                          filtrosPorOperacao={filtrosPorOperacao}
                          apenasProdutosPermissoesHabilitadas={
                            apenasProdutosPermissoesHabilitadas
                          }
                          produtos={produtos}
                          setFiltrado={setFiltrado}
                          filtrado={filtrado}
                        />
                      </TabPanel>
                    </Box>
                  );
                })}
          </Grid>
        </Box>
      </Box>
    );
  }
);
